/**
 * Site-wide Cloudflare Turnstile gate.
 *
 * Renders a full-screen "Verifying you are human…" overlay until the visitor
 * passes Turnstile, then unmounts and lets the rest of the React app render.
 * The pass is remembered for 30 minutes via localStorage; subsequent page
 * loads within that window skip the challenge.
 *
 * Wiring: wrap your top-level routes in <TurnstileSiteGate> in main.tsx /
 * app-router.tsx. The gate auto-skips:
 *   - admin / agent backoffice routes (so editors don't get re-challenged)
 *   - if no Turnstile site key is configured (so the site keeps working
 *     before keys are set up)
 *   - if the visitor has already passed within the TTL
 *
 * The token is sent to /api/v1/turnstile/verify which calls Cloudflare's
 * siteverify endpoint with the secret key — preventing a malicious user from
 * faking the pass by writing to localStorage.
 */
import {ReactNode, useEffect, useRef, useState} from 'react';
import lazyLoader from '@ui/utils/loaders/lazy-loader';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {apiClient} from '@common/http/query-client';

const STORAGE_KEY = 'tsg.pass.until';                 // localStorage key
// How long a visitor stays verified. This is our own "already passed" flag (the
// token itself is verified server-side once), so it doesn't need to match
// Cloudflare's short edge TTL — 30 min meant real visitors got re-challenged
// several times a day. A week keeps the gate rare while still stopping bots.
const PASS_TTL_MS = 7 * 24 * 60 * 60 * 1000;          // 7 days
const SCRIPT_ID   = 'turnstile-js';
const SCRIPT_URL  = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Routes that should NEVER show the gate. Admin dashboards stay challenge-free
// so editors don't get interrupted; API calls obviously can't render UI.
function shouldSkip(pathname: string): boolean {
  return (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/agent') ||
    pathname.startsWith('/api')   ||
    pathname.startsWith('/build') ||
    pathname.startsWith('/storage')
  );
}

function readPassUntil(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch { return 0; }
}

function writePassUntil(at: number) {
  try { localStorage.setItem(STORAGE_KEY, String(at)); } catch {}
}

export function TurnstileSiteGate({children}: {children: ReactNode}) {
  const settings = getBootstrapData().settings;
  const siteKey  = settings?.captcha?.t_site_key;
  const provider = settings?.captcha?.provider;

  // Pass if: not configured, on a skipped route, or already verified recently.
  const initialPass =
    !siteKey ||
    provider !== 'turnstile' ||
    shouldSkip(window.location.pathname) ||
    readPassUntil() > Date.now();

  const [passed, setPassed] = useState<boolean>(initialPass);

  if (passed) return <>{children}</>;
  return <TurnstileChallenge siteKey={siteKey!} onPass={() => {
    writePassUntil(Date.now() + PASS_TTL_MS);
    setPassed(true);
  }} />;
}

function TurnstileChallenge({siteKey, onPass}: {siteKey: string; onPass: () => void}) {
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const renderedRef = useRef(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || renderedRef.current) return;
      const ts = (window as any).turnstile;
      const el = widgetRef.current;
      if (!ts || !el) return;
      renderedRef.current = true;
      ts.render(el, {
        sitekey: siteKey,
        action:  'site-gate',
        theme:   'auto',
        callback: async (token: string) => {
          // Verify server-side so clients can't fake the pass by editing JS.
          try {
            const r = await apiClient.post('turnstile/verify', {token});
            if (r.data?.ok) {
              onPass();
            } else {
              setVerifyError(r.data?.message || 'Verification failed. Please try again.');
              ts.reset(el);
            }
          } catch (e: any) {
            setVerifyError(e?.message || 'Verification failed. Please try again.');
            ts.reset(el);
          }
        },
        'error-callback': () => setVerifyError('Could not load the security check. Reload the page.'),
        'expired-callback': () => ts.reset(el),
      });
    };

    // Load the Turnstile JS once. If it's already on the page, render
    // immediately; otherwise wait for it.
    if ((window as any).turnstile) {
      render();
    } else {
      lazyLoader.loadAsset(SCRIPT_URL, {id: SCRIPT_ID});
      const t = window.setInterval(() => {
        if ((window as any).turnstile) {
          window.clearInterval(t);
          render();
        }
      }, 100);
      return () => {
        cancelled = true;
        window.clearInterval(t);
      };
    }
    return () => { cancelled = true; };
  }, [siteKey, onPass]);

  // Minimal, friendly full-screen overlay. Inline styles so the gate works
  // before the app's CSS bundle has loaded.
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483647,
        background: '#f8fafc',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 420, width: '100%',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 10px 40px -10px rgba(15,23,42,0.15)',
          padding: 32, textAlign: 'center',
        }}
      >
        <h1 style={{fontSize: 18, margin: '0 0 8px', color: '#0f172a'}}>
          Performing security verification
        </h1>
        <p style={{fontSize: 13, color: '#64748b', margin: '0 0 24px', lineHeight: 1.55}}>
          This site uses Cloudflare to protect against malicious bots.
          This page is displayed while we verify you are not a bot.
        </p>
        <div ref={widgetRef} style={{display: 'flex', justifyContent: 'center'}} />
        {verifyError && (
          <p style={{marginTop: 16, fontSize: 12, color: '#dc2626'}}>{verifyError}</p>
        )}
        <p style={{marginTop: 24, fontSize: 11, color: '#94a3b8'}}>
          Powered by Cloudflare Turnstile
        </p>
      </div>
    </div>
  );
}
